import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { AuthProvider } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CallProvider } from "@/context/CallContext";
import { ConsumerLayout } from "@/components/ConsumerLayout";
import { CommandCenterLayout } from "@/components/CommandCenterLayout";
import { ComingSoon } from "@/components/ComingSoon";
import {
  IdentificationCard, AddressBook, BellRinging,
} from "@phosphor-icons/react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Home from "@/pages/Home";
import Wallet from "@/pages/Wallet";
import Numbers from "@/pages/Numbers";
import TopUp from "@/pages/TopUp";
import Esim from "@/pages/Esim";
import Broadcast from "@/pages/Broadcast";
import Billing from "@/pages/Billing";
import More from "@/pages/More";
import Profile from "@/pages/Profile";
import Call from "@/pages/Call";
import Pbx from "@/pages/Pbx";
import Recordings from "@/pages/Recordings";
import Meet from "@/pages/Meet";
import MeetBranding from "@/pages/MeetBranding";
import BroadcastCenter from "@/pages/BroadcastCenter";
import MeetAnalytics from "@/pages/MeetAnalytics";
import Watch from "@/pages/Watch";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Text from "@/pages/Text";
import Appointments from "@/pages/Appointments";
import Match from "@/pages/Match";
import BusinessHunter from "@/pages/BusinessHunter";
import Telephony from "@/pages/Telephony";
import Fax from "@/pages/Fax";
import MediaStudio from "@/pages/MediaStudio";
import DigitalServices from "@/pages/DigitalServices";
import Products from "@/pages/Products";
import WhatsApp from "@/pages/WhatsApp";
import Lookup from "@/pages/Lookup";
import SendMoney from "@/pages/SendMoney";
import JoinMeet from "@/pages/JoinMeet";
import QuickBuy from "@/pages/QuickBuy";
import Rates from "@/pages/Rates";
import Services from "@/pages/Services";
import Pricing from "@/pages/Pricing";
import Welcome from "@/pages/Welcome";
import Refer from "@/pages/Refer";
import DeviceSettings from "@/pages/DeviceSettings";
import ShopDevices from "@/pages/ShopDevices";
import AdminShop from "@/pages/admin/AdminShop";
import AdminPlans from "@/pages/admin/AdminPlans";
import DialerApp from "@/pages/DialerApp";
import Wireless from "@/pages/Wireless";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import AdminTelecomRates from "@/pages/admin/AdminTelecomRates";
import AdminTelecomOrders from "@/pages/admin/AdminTelecomOrders";
import ResellerPortal from "@/pages/ResellerPortal";
import Rides from "@/pages/Rides";
import Eat from "@/pages/Eat";
import Fleet from "@/pages/Fleet";
import Freelance from "@/pages/Freelance";
import Inspector from "@/pages/Inspector";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import FinishSignup from "@/pages/FinishSignup";
import GiftCards from "@/pages/GiftCards";
import Airtime from "@/pages/Airtime";
import Notifications from "@/pages/Notifications";
import AdminPricing from "@/pages/admin/AdminPricing";
import VerticalLanding from "@/pages/VerticalLanding";
import Book from "@/pages/Book";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";

import CommandCenter from "@/pages/admin/CommandCenter";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminProviders from "@/pages/admin/AdminProviders";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminOrgs from "@/pages/admin/AdminOrgs";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminBrand from "@/pages/admin/AdminBrand";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMessaging from "@/pages/admin/AdminMessaging";
import AdminEsimReview from "@/pages/admin/AdminEsimReview";
import AdminMatchPackages from "@/pages/admin/AdminMatchPackages";
import AdminAiOutreach from "@/pages/admin/AdminAiOutreach";
import AdminJourneys from "@/pages/admin/AdminJourneys";
import AdminSalesCatalog from "@/pages/admin/AdminSalesCatalog";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminRates from "@/pages/admin/AdminRates";
import AdminRides from "@/pages/admin/AdminRides";
import AdminEat from "@/pages/admin/AdminEat";
import AdminFleet from "@/pages/admin/AdminFleet";
import AdminFreelance from "@/pages/admin/AdminFreelance";
import AdminMeet from "@/pages/admin/AdminMeet";
import AdminTopupMargins from "@/pages/admin/AdminTopupMargins";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import PbxExtensions from "@/pages/admin/PbxExtensions";
import Directory from "@/pages/Directory";
import Business from "@/pages/Business";
import AISales from "@/pages/AISales";
import AISalesPlans from "@/pages/AISalesPlans";
import AdminAISales from "@/pages/admin/AdminAISales";
import AdminNumbers from "@/pages/admin/AdminNumbers";
import { VersionStamp } from "@/components/VersionStamp";
import AdminAISalesPackages from "@/pages/admin/AdminAISalesPackages";
import AdminKnowledge from "@/pages/admin/AdminKnowledge";
import AdminClosingConfig from "@/pages/admin/AdminClosingConfig";
import AdminLiveConnect from "@/pages/admin/AdminLiveConnect";

const Consumer = ({ children }) => (
  <ProtectedRoute><ConsumerLayout>{children}</ConsumerLayout></ProtectedRoute>
);
const Command = ({ children }) => (
  <ProtectedRoute staffOnly><CommandCenterLayout>{children}</CommandCenterLayout></ProtectedRoute>
);

function App() {
  return (
    <BrandProvider>
      <AuthProvider>
        <BrowserRouter>
          <CallProvider>
          <ImpersonationBanner />
          <MaintenanceBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/finish-signup" element={<FinishSignup />} />
            <Route path="/receptionist/:vertical" element={<VerticalLanding />} />
            <Route path="/receptionist" element={<VerticalLanding />} />
            <Route path="/book" element={<Book />} />
            <Route path="/join" element={<JoinMeet />} />
            <Route path="/watch/:code" element={<Watch />} />
            <Route path="/buy" element={<QuickBuy />} />
            <Route path="/rates" element={<Rates />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/shop" element={<ShopDevices standalone />} />
            <Route path="/shop/:slug" element={<ProductDetail />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/wireless" element={<Wireless />} />
            <Route path="/plans" element={<Wireless />} />
            <Route path="/dialer" element={<ProtectedRoute><DialerApp /></ProtectedRoute>} />
            <Route path="/services" element={<Services />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/app" element={<Consumer><Home /></Consumer>} />
            <Route path="/app/wallet" element={<Consumer><Wallet /></Consumer>} />
            <Route path="/app/giftcards" element={<Consumer><GiftCards /></Consumer>} />
            <Route path="/app/airtime" element={<Consumer><Airtime /></Consumer>} />
            <Route path="/app/numbers" element={<Consumer><Numbers /></Consumer>} />
            <Route path="/app/topup" element={<Consumer><TopUp /></Consumer>} />
            <Route path="/app/esim" element={<Consumer><Esim /></Consumer>} />
            <Route path="/app/broadcast" element={<Consumer><Broadcast /></Consumer>} />
            <Route path="/app/billing" element={<Consumer><Billing /></Consumer>} />
            <Route path="/app/more" element={<Consumer><More /></Consumer>} />
            <Route path="/app/profile" element={<Consumer><Profile /></Consumer>} />

            <Route path="/app/call" element={<Consumer><Call /></Consumer>} />
            <Route path="/app/directory" element={<Consumer><Directory /></Consumer>} />
            <Route path="/app/business" element={<Consumer><Business /></Consumer>} />
            <Route path="/app/ai-sales" element={<Consumer><AISales /></Consumer>} />
            <Route path="/app/ai-sales/plans" element={<Consumer><AISalesPlans /></Consumer>} />
            <Route path="/app/text" element={<Consumer><Text /></Consumer>} />
            <Route path="/app/appointments" element={<Consumer><Appointments /></Consumer>} />
            <Route path="/app/match" element={<Consumer><Match /></Consumer>} />
            <Route path="/app/ai-sales/hunter" element={<Consumer><BusinessHunter /></Consumer>} />
            <Route path="/app/telephony" element={<Consumer><Telephony /></Consumer>} />
            <Route path="/app/meet" element={<Consumer><Meet /></Consumer>} />
            <Route path="/app/meet/branding" element={<Consumer><MeetBranding /></Consumer>} />
            <Route path="/app/meet/broadcast" element={<Consumer><BroadcastCenter /></Consumer>} />
            <Route path="/app/meet/analytics" element={<Consumer><MeetAnalytics /></Consumer>} />
            <Route path="/app/pbx" element={<Consumer><Pbx /></Consumer>} />
            <Route path="/app/recordings" element={<Consumer><Recordings /></Consumer>} />
            <Route path="/app/fax" element={<Consumer><Fax /></Consumer>} />
            <Route path="/app/media" element={<Consumer><MediaStudio /></Consumer>} />
            <Route path="/app/services" element={<Consumer><DigitalServices /></Consumer>} />
            <Route path="/app/products" element={<Consumer><Products /></Consumer>} />
            <Route path="/app/whatsapp" element={<Consumer><WhatsApp /></Consumer>} />
            <Route path="/app/lookup" element={<Consumer><Lookup /></Consumer>} />
            <Route path="/app/send" element={<Consumer><SendMoney /></Consumer>} />
            <Route path="/app/refer" element={<Consumer><Refer /></Consumer>} />
            <Route path="/app/reseller" element={<Consumer><ResellerPortal /></Consumer>} />
            <Route path="/app/rides" element={<Consumer><Rides /></Consumer>} />
            <Route path="/app/eat" element={<Consumer><Eat /></Consumer>} />
            <Route path="/app/fleet" element={<Consumer><Fleet /></Consumer>} />
            <Route path="/app/freelance" element={<Consumer><Freelance /></Consumer>} />
            <Route path="/app/inspector" element={<Consumer><Inspector /></Consumer>} />
            <Route path="/app/contacts" element={<Consumer><ComingSoon title="Contacts" phase="Phase 2" icon={AddressBook} description="Centralised contacts with tags, groups and full communication history." /></Consumer>} />
            <Route path="/app/device" element={<Consumer><DeviceSettings /></Consumer>} />
            <Route path="/app/shop" element={<Consumer><ShopDevices /></Consumer>} />
            <Route path="/app/sip" element={<Consumer><DeviceSettings /></Consumer>} />
            <Route path="/app/notifications" element={<Consumer><Notifications /></Consumer>} />

            <Route path="/command" element={<Command><CommandCenter /></Command>} />
            <Route path="/command/users" element={<Command><AdminUsers /></Command>} />
            <Route path="/command/leads" element={<Command><AdminLeads /></Command>} />
            <Route path="/command/ai-sales" element={<Command><AdminAISales /></Command>} />
            <Route path="/command/ai-sales/packages" element={<Command><AdminAISalesPackages /></Command>} />
            <Route path="/command/ai-sales/knowledge" element={<Command><AdminKnowledge /></Command>} />
            <Route path="/command/ai-sales/closing" element={<Command><AdminClosingConfig /></Command>} />
            <Route path="/command/ai-sales/automation" element={<Command><AdminAiOutreach /></Command>} />
            <Route path="/command/ai-sales/journeys" element={<Command><AdminJourneys /></Command>} />
            <Route path="/command/ai-sales/catalog" element={<Command><AdminSalesCatalog /></Command>} />
            <Route path="/command/ai-sales/revenue" element={<Command><AdminRevenue /></Command>} />
            <Route path="/command/providers" element={<Command><AdminProviders /></Command>} />
            <Route path="/command/catalog" element={<Command><AdminCatalog /></Command>} />
            <Route path="/command/organizations" element={<Command><AdminOrgs /></Command>} />
            <Route path="/command/audit" element={<Command><AdminAudit /></Command>} />
            <Route path="/command/brand" element={<Command><AdminBrand /></Command>} />
            <Route path="/command/settings" element={<Command><AdminSettings /></Command>} />
            <Route path="/command/messaging" element={<Command><AdminMessaging /></Command>} />
            <Route path="/command/esim-review" element={<Command><AdminEsimReview /></Command>} />
            <Route path="/command/match-packages" element={<Command><AdminMatchPackages /></Command>} />
            <Route path="/command/rates" element={<Command><AdminRates /></Command>} />
            <Route path="/command/rides" element={<Command><AdminRides /></Command>} />
            <Route path="/command/eat" element={<Command><AdminEat /></Command>} />
            <Route path="/command/fleet" element={<Command><AdminFleet /></Command>} />
            <Route path="/command/freelance" element={<Command><AdminFreelance /></Command>} />
            <Route path="/command/meet" element={<Command><AdminMeet /></Command>} />
            <Route path="/command/topup-margins" element={<Command><AdminTopupMargins /></Command>} />
            <Route path="/command/payouts" element={<Command><AdminPayouts /></Command>} />
            <Route path="/command/pbx" element={<Command><PbxExtensions /></Command>} />
            <Route path="/command/telecom-rates" element={<Command><AdminTelecomRates /></Command>} />
            <Route path="/command/telecom-orders" element={<Command><AdminTelecomOrders /></Command>} />
            <Route path="/command/shop" element={<Command><AdminShop /></Command>} />
            <Route path="/command/plans" element={<Command><AdminPlans /></Command>} />
            <Route path="/command/numbers" element={<Command><AdminNumbers /></Command>} />
            <Route path="/command/live-connect" element={<Command><AdminLiveConnect /></Command>} />
            <Route path="/command/pricing" element={<Command><AdminPricing /></Command>} />

            <Route path="*" element={<Landing />} />
          </Routes>
          <Toaster position="top-center" richColors offset="96px" />
          <VersionStamp />
          </CallProvider>
        </BrowserRouter>
      </AuthProvider>
    </BrandProvider>
  );
}

export default App;
